/* eslint-disable max-statements */
/* eslint-disable no-lonely-if */
/* eslint-disable max-depth */
/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import { Axis } from '../../../spatial-classes';
import { getPanelToIslandResolver } from '../helpers';
import { GetIntersectionAreasOptions } from '../types';
import { IntersectionArea } from '../../shared.types';
import { isHorizontalRightIntersect } from '.';
import { nearestSixteenth } from '../../../helpers';
import { Panel } from '../panel.class';
import { PanelItem } from '../../panel-items/panel-item.class';
import { COMPONENT_PLACEMENT_FRONT_AND_TOP, COMPONENT_PLACEMENT_TOP_ONLY } from '../../../component-classes/constants';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getHorizontalIntersectionAreasCfn = (panel: Panel, options: GetIntersectionAreasOptions): Array<IntersectionArea> => {
	const { inside = false, includeDuplicates = true, includeEnd2End = true, minHeight = 0, minWidth = 0, isCountertop = false } = options;
	const panelItems: Array<{
		i: number;
		pi:PanelItem,
		top: number,
		bottom: number,
		left: number,
		right: number,
		horizontalId: number,
		shouldHide: boolean,
		isCircularCutout: boolean,
		visibleY?: number
	}> = [];
	// console.log('getHorizontalOverlaps', { panel, inside });
	// const { island } = panel.refMap;
	let index = 1;
	const matched: IntersectionArea[] = [];

	// -------------------------------
	// build a list of the panel items that are on the panel
	// -------------------------------
	// The countertop draws every panel's item groups, so it must enumerate every
	// panel's items (a top-only cutout added through the back/side panel otherwise
	// gets no dimensions). The panel-face view still measures only its own panel.
	const itemGroups = isCountertop
		? panel.refMap.island.panels.children.flatMap((itemPanel) => itemPanel.panelItemGroups.children)
		: panel.panelItemGroups.children;
	itemGroups.forEach((currGrp) => {
		currGrp.items.children.forEach((pi) => {
			if (pi.item.category?.isSpacer) return;
			if ((pi.item.component.dimensions.outside.horizontal.ignore && !inside) || (pi.item.component.dimensions.inside.horizontal.ignore && inside)) return;
			if (isCountertop && (pi.item.placement === COMPONENT_PLACEMENT_TOP_ONLY || pi.item.placement === COMPONENT_PLACEMENT_FRONT_AND_TOP) && pi.sleeve === null) {
				// MFG (cutout-only) view never draws a non-cutting top-only item, so exclude it from gap matching too, or it occludes the real gap between its neighbors.
				if (inside && pi.item.placement === COMPONENT_PLACEMENT_TOP_ONLY && !pi.item.distanceBelowCountertop) return;

				const { top: corners } = pi.getCorners(inside);
				/* topCorners = [front-left, front-right, back-right, back-left] */

				const isUmbrellaCutout = pi.item.sku.startsWith('CUSTOM_UMBRELLA_CT-');
				const isGasLineCutout = pi.item.sku.startsWith('CUSTOM_GASOLINE_CT-') || pi.item.sku === 'GAS_LINE_CUTOUT' || pi.item.label === 'GAS_LINE_CUTOUT';
				const isMokPassthrough = pi.item.sku === 'MOK-PASSTHROUGH';
				const isHoleCategory = pi.item.category?.key === 'hole';
				const isCircularCutout = isUmbrellaCutout || isGasLineCutout || isMokPassthrough || isHoleCategory;
				const groupCenterX = pi.refMap?.panelItemGroup?.center.x ?? 0;
				// Where the SVG actually renders for this panel item — same as
				// the visible circle center. Without `pi.center.x`, a tap that
				// sits offset inside the kegerator's group would dimension to
				// the group center instead of the visible cutout.
				const itemCenterX = groupCenterX + (pi.center.x ?? 0);

				// Transform panel-local (width, depth) into island/countertop space.
				const itemPanel = pi.refMap?.panel ?? panel;
				const resolveToIsland = getPanelToIslandResolver(itemPanel);
				const toIsland = (localX: number, localZ: number): { x: number; y: number } => {
					const { x, z } = resolveToIsland(localX, localZ);
					return { x, y: z };
				};

				// dimensions measure to the center of the circle
				const leftLocalX = isCircularCutout ? itemCenterX : corners[0].x + groupCenterX;
				const rightLocalX = isCircularCutout ? itemCenterX : corners[1].x + groupCenterX;
				const backLocalZ = corners[0].y + pi.center.z;
				const frontLocalZ = corners[3].y + pi.center.z;

				const box = [
					toIsland(leftLocalX, backLocalZ),
					toIsland(rightLocalX, backLocalZ),
					toIsland(rightLocalX, frontLocalZ),
					toIsland(leftLocalX, frontLocalZ)
				];
				const xs = box.map((p) => p.x);
				const ys = box.map((p) => p.y);
				const visible = toIsland(isCircularCutout ? itemCenterX : (leftLocalX + rightLocalX) / 2, pi.center.z);

				panelItems.push({
					i: index,
					pi,
					// top/bottom remain corner-based so the area's vertical extent
					// stays correct for overlap math. The dim LINE's Y position
					// is overridden below in the match-creation block using
					// `visibleY` for circular cutouts.
					top: Math.min(...ys),
					bottom: Math.max(...ys),
					left: isCircularCutout ? visible.x : Math.min(...xs),
					right: isCircularCutout ? visible.x : Math.max(...xs),
					horizontalId: groupCenterX,
					shouldHide: false,
					isCircularCutout,
					// Y of the visible SVG center — used only when this entry is
					// part of a match involving a circular cutout, to draw the
					// dim line through the visible circle (not the corner-avg).
					visibleY: visible.y
				});
				index++;
			} else if (!isCountertop && pi.item.placement !== COMPONENT_PLACEMENT_TOP_ONLY && pi.sleeve === null) {
				const { front: corners } = pi.getCorners(inside);
				/* frontCorners = [top-left, top-right, bottom-right, bottom-left] */
				const centerY = pi.getCenter(true).y;

				// Check label, SKU prefix, and category for circular cutouts
				const isUmbrellaCutout = pi.item.label === 'UMBRELLA-CUTOUT' || pi.item.sku.startsWith('CUSTOM_UMBRELLA');
				const isGasLineCutout = pi.item.label === 'GASOLINE-CUTOUT' || pi.item.label === 'GAS_LINE_CUTOUT' || pi.item.sku.startsWith('CUSTOM_GASOLINE') || pi.item.sku === 'GAS_LINE_CUTOUT';
				const isMokPassthrough = pi.item.sku === 'MOK-PASSTHROUGH';
				const isHoleCategory = pi.item.category?.key === 'hole';
				const isCircularCutout = isUmbrellaCutout || isGasLineCutout || isMokPassthrough || isHoleCategory;
				const groupCenterX = pi.refMap?.panelItemGroup?.center.x ?? 0;

				// dimensions measure to the center of the circle
				// snap the EDGE, not the span -- gaps are then differences of sixteenths, so each stays on grid AND they still sum to the overall
				const leftX = isCircularCutout
					? nearestSixteenth(groupCenterX)
					: nearestSixteenth(corners[0].x + groupCenterX);
				const rightX = isCircularCutout
					? nearestSixteenth(groupCenterX)
					: nearestSixteenth(corners[1].x + groupCenterX);

				panelItems.push({
					i: index,
					pi,
					top: corners[0].y + centerY + currGrp.center.y,
					bottom: corners[3].y + centerY + currGrp.center.y,
					left: leftX,
					right: rightX,
					horizontalId: groupCenterX,
					shouldHide: false,
					isCircularCutout,
					visibleY: centerY + currGrp.center.y
				});
				index++;
			}
		});
	});

	// -------------------------------
	// flag hidden dimensions
	// -------------------------------

	// -------------------------------
	// add the panel edges to the list
	// -------------------------------

	let sortedItems = [];
	let countertop = null;

	if (isCountertop) {
		countertop = panel.refMap.island.getCountertop();
		const countertopCorners = countertop.renderingDetails.vertices;
		// For standard islands (4 vertices): [front-left, back-left, back-right, front-right]
		// For corner islands (5 vertices): [front, leftSide, backLeft, backRight, rightSide]
		// Use first and last vertices for the front-left and front-right edges respectively.
		const lastIdx = countertopCorners.length - 1;
		const leftVertex = countertopCorners[0];
		const leftBackVertex = countertopCorners[1];
		const rightBackVertex = countertopCorners[lastIdx - 1];
		const rightVertex = countertopCorners[lastIdx];

		sortedItems = [
			// left edge
			{ i: 0, pi: countertop.id, top: leftBackVertex.y, bottom: leftVertex.y, left: leftVertex.x, right: leftVertex.x },
			// items
			...panelItems.sort((a, b) => a.left - b.left),
			// right edge
			{ i: index, pi: countertop.id, top: rightBackVertex.y, bottom: rightVertex.y, left: rightVertex.x, right: rightVertex.x }
		];
	} else {
		// Bug 10: hybrid measures item gaps from the panel's own face corners (like MFG), so a centered outlet reads centered -- getSidePoints' one-sided joint padding skews it, and getCornerPoints never touches connection-joint dims (drawn from getSidePoints elsewhere).
		const pCorners = inside
			? panel.getCornerPoints(inside, { translate: false, nearestSixteenth: true }, new Set()).corners
			: panel.getCornerPoints(false, { translate: false, rotate: false, nearestSixteenth: true }, new Set()).corners;

		sortedItems = [
			// left edge
			{ i: 0, pi: panel.id, top: pCorners[0].y, bottom: pCorners[3].y, left: pCorners[0].x, right: pCorners[0].x },
			// items
			...panelItems.sort((a, b) => a.left - b.left),
			// right edge
			{ i: index, pi: panel.id, top: pCorners[1].y, bottom: pCorners[2].y, left: pCorners[1].x, right: pCorners[1].x }
		];
	}

	// -------------------------------
	// foreach sortedItem, find everything that overlaps to the right
	sortedItems.forEach((sItem) => {
		let ranges = [sItem];
		let nextRanges = [];

		// find items overlapping to the right of sItem
		const rItems = sortedItems.filter((f) => isHorizontalRightIntersect(sItem, f));

		for (const nItem of rItems) {
			// console.log(`nItem ${nItem.pi?.item.sku ?? 'null'}`, ranges);
			for (const range of ranges) {
				if (isHorizontalRightIntersect(range, nItem)) {
					// console.log(`sItem ${sItem.pi?.item.sku ?? 'null'} -> ${nItem.pi?.item.sku ?? 'null'}:`, { sItem, nItem, ranges, range });
					const sourceId = (typeof sItem.pi) === 'string' ? sItem.pi : sItem.pi.id;
					const targetId = (typeof nItem.pi) === 'string' ? nItem.pi : nItem.pi.id;
					// const sourceId = sItem.pi?.id ?? panel.id;
					// const targetId = nItem.pi?.id ?? panel.id;
					const top = Math.max(nItem.top, range.top);
					const bottom = Math.min(nItem.bottom, range.bottom);
					let size = null;

					size = {
						height: bottom - top,
						width: nItem.left - sItem.right
					};

					const isDuplicate = matched.some((m) => {
						return m.intersectionReferenceIds[0] === sourceId && m.intersectionReferenceIds[1] === targetId;
					});

					// Circular cutouts use center points so height may be 0, but dimensions should still render
					const involvesCircularCutout = sItem.isCircularCutout || nItem.isCircularCutout;
					// For a circular cutout the SVG renders at the panel item's own
					// position, not at the corner-derived middle of the cutout box.
					// `corners[*].y + pi.center.z` bakes in `zCenterAdjustment` which
					// shifts the corner Y when `inside=true` for items whose size is
					// larger than their cutout (kegtap: 17.01 vs 2.75). Use the
					// circular item's `visibleY` for the dim line's Y so it draws
					// through the visible circle in both hybrid and MFG views.
					const nItemVisibleY = (nItem as { visibleY?: number }).visibleY;
					const sItemVisibleY = (sItem as { visibleY?: number }).visibleY;
					const circularVisibleY = nItem.isCircularCutout && nItemVisibleY != null
						? nItemVisibleY
						: sItem.isCircularCutout && sItemVisibleY != null
							? sItemVisibleY
							: null;

					// console.log('isDuplicate', isDuplicate, matched);
					// 	piSource: sItem.pi,
					// 	piTarget: nItem.pi,
					// 	top,
					// 	bottom,
					// 	left: sItem.right,
					// 	right: nItem.left,
					// 	height: bottom - top,
					// 	width: nItem.left - sItem.right
					if (isCountertop) {
						const heightCheck = involvesCircularCutout ? size.height >= 0 : size.height > 0;
						const widthCheck = involvesCircularCutout ? size.width >= 0 : size.width > 0;
						const minHeightCheck = involvesCircularCutout || minHeight === 0 || size.height >= minHeight;
						const minWidthCheck = involvesCircularCutout || minWidth === 0 || size.width >= minWidth;
						if (heightCheck && widthCheck &&
							(!isDuplicate || (isDuplicate && includeDuplicates)) &&
							(includeEnd2End || (!includeEnd2End && (sourceId !== countertop.id || targetId !== countertop.id))) &&
							minHeightCheck &&
							minWidthCheck
						) {
							matched.push({
								intersectionReferenceIds: [sourceId, targetId, null],
								axis: new Axis({ dataIn: { z: 0 }}),
								origin: {
									x: sItem.right + (size.width / 2),
									y: circularVisibleY ?? (top + bottom) / 2
								},
								size
							} as IntersectionArea);
						}
					} else {
						const heightCheck = involvesCircularCutout ? size.height >= 0 : size.height > 0;
						const widthCheck = involvesCircularCutout ? size.width >= 0 : size.width > 0;
						const minHeightCheck = involvesCircularCutout || minHeight === 0 || size.height >= minHeight;
						const minWidthCheck = involvesCircularCutout || minWidth === 0 || size.width >= minWidth;
						if (heightCheck && widthCheck &&
							(!isDuplicate || (isDuplicate && includeDuplicates)) &&
							(includeEnd2End || (!includeEnd2End && (sourceId !== panel.id || targetId !== panel.id))) &&
							minHeightCheck &&
							minWidthCheck
						) {
							matched.push({
								intersectionReferenceIds: [sourceId, targetId, null],
								axis: new Axis({ dataIn: { z: 0 }}),
								origin: {
									x: sItem.right + (size.width / 2),
									y: circularVisibleY ?? top + (size.height / 2)
								},
								size
							} as IntersectionArea);
						}
					}

					if (top > range.top) nextRanges.push({ ...sItem, top: range.top, bottom: top });
					if (bottom < range.bottom) nextRanges.push({ ...sItem, top: bottom, bottom: range.bottom });
				} else {
					// push for next appliance check
					nextRanges.push(range);
				}
			}

			if (nextRanges.length === 0) break;
			ranges = [...nextRanges];
			nextRanges = [];
		}
	});

	// Re-emit a pass-through's dimension to its nearest panel end when a vent between them (fully overlapping its band) claimed the edge-adjacent gap and suppressed it -- bridge across vents only, never across a real appliance.
	const [leftPanelEdge] = sortedItems;
	const rightPanelEdge = sortedItems[sortedItems.length - 1];
	const leftPanelEdgeId = typeof leftPanelEdge.pi === 'string' ? leftPanelEdge.pi : leftPanelEdge.pi.id;
	const rightPanelEdgeId = typeof rightPanelEdge.pi === 'string' ? rightPanelEdge.pi : rightPanelEdge.pi.id;

	panelItems.forEach((passthrough) => {
		const { sku } = passthrough.pi.item;
		if (sku !== 'PASSTHROUGH' && sku !== 'MOK-PASSTHROUGH') return;

		const distanceToLeft = passthrough.left - leftPanelEdge.right;
		const distanceToRight = rightPanelEdge.left - passthrough.right;
		const nearestIsLeft = distanceToLeft <= distanceToRight;
		const width = nearestIsLeft ? distanceToLeft : distanceToRight;
		if (width <= 0) return;

		const sourceId = nearestIsLeft ? leftPanelEdgeId : passthrough.pi.id;
		const targetId = nearestIsLeft ? passthrough.pi.id : rightPanelEdgeId;

		const alreadyEmitted = matched.some((m) => m.intersectionReferenceIds[0] === sourceId && m.intersectionReferenceIds[1] === targetId);
		if (alreadyEmitted) return;

		const blockedByAppliance = panelItems.some((other) => {
			if (other.pi.id === passthrough.pi.id || other.pi.item.category?.isVent) return false;
			const verticallyOverlaps = other.top < passthrough.bottom && other.bottom > passthrough.top;
			return verticallyOverlaps && (nearestIsLeft ? other.left < passthrough.left : other.right > passthrough.right);
		});
		if (blockedByAppliance) return;

		const originX = nearestIsLeft ? leftPanelEdge.right + (width / 2) : passthrough.right + (width / 2);
		const originY = passthrough.isCircularCutout && passthrough.visibleY != null
			? passthrough.visibleY
			: passthrough.top + ((passthrough.bottom - passthrough.top) / 2);

		matched.push({
			intersectionReferenceIds: [sourceId, targetId, null],
			axis: new Axis({ dataIn: { z: 0 }}),
			origin: {
				x: originX,
				y: originY
			},
			size: {
				width,
				height: passthrough.bottom - passthrough.top
			}
		} as IntersectionArea);
	});

	// When a pass-through coincides with a vent at the vent's nearest edge and overlaps its band, the range sweep hands the single shared-edge gap to only one of them; if the pass-through won it, re-emit the vent's edge-to-panel dimension so the vent's is always present (the component then collapses the pass-through's identical copy onto it).
	const passthroughSpans = panelItems.filter((item) => item.pi.item.sku === 'PASSTHROUGH' || item.pi.item.sku === 'MOK-PASSTHROUGH');

	panelItems.forEach((vent) => {
		if (!vent.pi.item.category?.isVent) return;

		const distanceToLeft = vent.left - leftPanelEdge.right;
		const distanceToRight = rightPanelEdge.left - vent.right;
		const nearestIsLeft = distanceToLeft <= distanceToRight;
		const width = nearestIsLeft ? distanceToLeft : distanceToRight;
		if (width <= 0) return;

		const sourceId = nearestIsLeft ? leftPanelEdgeId : vent.pi.id;
		const targetId = nearestIsLeft ? vent.pi.id : rightPanelEdgeId;

		const alreadyEmitted = matched.some((m) => m.intersectionReferenceIds[0] === sourceId && m.intersectionReferenceIds[1] === targetId);
		if (alreadyEmitted) return;

		const coincidesWithPassthrough = passthroughSpans.some((p) => {
			const verticallyOverlaps = p.top < vent.bottom && p.bottom > vent.top;
			return verticallyOverlaps && (nearestIsLeft ? p.left === vent.left : p.right === vent.right);
		});
		if (!coincidesWithPassthrough) return;

		const blockedByAppliance = panelItems.some((other) => {
			if (other.pi.id === vent.pi.id || other.pi.item.category?.isVent) return false;
			const verticallyOverlaps = other.top < vent.bottom && other.bottom > vent.top;
			return verticallyOverlaps && (nearestIsLeft ? other.left < vent.left : other.right > vent.right);
		});
		if (blockedByAppliance) return;

		matched.push({
			intersectionReferenceIds: [sourceId, targetId, null],
			axis: new Axis({ dataIn: { z: 0 }}),
			origin: {
				x: nearestIsLeft ? leftPanelEdge.right + (width / 2) : vent.right + (width / 2),
				y: vent.top + ((vent.bottom - vent.top) / 2)
			},
			size: {
				width,
				height: vent.bottom - vent.top
			}
		} as IntersectionArea);
	});

	// Add Breakpoints

	const bpList = isCountertop ? panel.refMap.island.getCountertop().breakpoints : panel.breakpoints;
	const matchedWithBreakpoints = [];

	matched.forEach((m) => {
		const hw = m.size.width / 2;
		// deliberately NOT snapped -- corner-island countertop vertices are sqrt(2) derived and must keep full precision
		const leftEdge = m.origin.x - hw;
		const rightEdge = m.origin.x + hw;

		const breakpoints = bpList.children.filter((bp) => bp.center >= leftEdge && bp.center <= rightEdge);

		let [lastRef] = m.intersectionReferenceIds;
		// had breakpoint
		if (breakpoints) {
			let lastX = leftEdge;
			// create a dimension item for each segment within the breakpoint
			breakpoints.forEach((bp) => {
				matchedWithBreakpoints.push({
					...m,
					intersectionReferenceIds: [lastRef, bp.id, lastRef === m.intersectionReferenceIds[1] ? null : m.intersectionReferenceIds[1]],
					origin: {
						x: lastX + ((bp.center - lastX) / 2),
						y: m.origin.y
					},
					size: {
						width: bp.center - lastX,
						height: m.size.height
					}
				} as IntersectionArea);

				lastX = bp.center;
				lastRef = bp.id;
			});

			// inject the last breakpoint dimension segment
			matchedWithBreakpoints.push({
				...m,
				intersectionReferenceIds: [lastRef, m.intersectionReferenceIds[1], lastRef === m.intersectionReferenceIds[0] ? null : m.intersectionReferenceIds[0]],
				origin: {
					x: lastX + ((rightEdge - lastX) / 2),
					y: m.origin.y
				},
				size: {
					width: rightEdge - lastX,
					height: m.size.height
				}
			} as IntersectionArea);
		}
	});
	const intersects: IntersectionArea[] = [];
	matchedWithBreakpoints.forEach((m) => {
		const isDuplicate = intersects.some((i) => {
			return m.intersectionReferenceIds[0] === i.intersectionReferenceIds[0] && m.intersectionReferenceIds[1] === i.intersectionReferenceIds[1];
		});
		if (!isDuplicate || (isDuplicate && includeDuplicates)) intersects.push(m);
	})

	return intersects;
};
